/**
 * WordprocessingML body parsing and surgical paragraph rewriting.
 *
 * Two things make this module different from a generic DOCX-to-HTML converter.
 *
 * **It records where each paragraph came from.** Every parsed paragraph keeps
 * the byte span it occupies in `word/document.xml`. Saving an edit replaces
 * that span and leaves the rest of the part — and every other part in the
 * package — exactly as it was. Section properties, headers, styles, embedded
 * fonts, macros, tracked changes elsewhere in the file: none of it is parsed on
 * the save path, so none of it can be lost there (§13, §24).
 *
 * **It records what it could not represent.** A paragraph holding a picture, a
 * field, or a footnote reference cannot survive being rebuilt from plain text,
 * so it is marked as not editable rather than being quietly flattened. §19
 * forbids offering an affordance the implementation cannot honour, and the
 * cheapest way to keep that promise is to compute it while parsing.
 *
 * The parser is a scanner over the XML text rather than a DOM build. A Word
 * document is a long flat sequence of `<w:p>` and `<w:tbl>` elements; scanning
 * keeps memory proportional to the output instead of to the markup, and gives
 * the byte offsets the save path needs.
 */
import { HandlerError } from '../contract.js';
import { escapeXml, unescapeXml } from '../ooxml/xml.js';

export type RevisionMark = 'inserted' | 'deleted';

export type DocxRun =
  | {
    readonly kind: 'text';
    readonly text: string;
    readonly bold: boolean;
    readonly italic: boolean;
    readonly underline: boolean;
    readonly strike: boolean;
    readonly vertical: 'super' | 'sub' | null;
    readonly highlight: string | null;
    readonly color: string | null;
    /** Target of the enclosing hyperlink, already resolved through the rels. */
    readonly href: string | null;
    /** Set when the run sits inside `<w:ins>` or `<w:del>` (§14). */
    readonly revision: RevisionMark | null;
  }
  | { readonly kind: 'break' }
  | { readonly kind: 'tab' }
  | {
    readonly kind: 'image';
    readonly relId: string;
    /** Package part the relationship resolves to, when it could be resolved. */
    readonly part: string | null;
    readonly widthEmu: number;
    readonly heightEmu: number;
    readonly alt: string;
  }
  | {
    /** A field result — page numbers, cross references, TOC entries. */
    readonly kind: 'field';
    readonly instruction: string;
    readonly text: string;
  };

export interface DocxParagraph {
  readonly kind: 'paragraph';
  /** Position in document order. Stable for as long as the file is unchanged. */
  readonly index: number;
  readonly styleId: string | null;
  readonly styleName: string | null;
  readonly headingLevel: number | null;
  readonly alignment: 'left' | 'center' | 'right' | 'justify' | null;
  readonly listLevel: number | null;
  readonly listOrdered: boolean;
  readonly runs: readonly DocxRun[];
  /** Flattened text, the same string search and copy operate on. */
  readonly text: string;
  /**
   * The text this paragraph had in the file on disk.
   *
   * Kept separately from {@link text} because an edit updates one and not the
   * other: typing something and then typing the original back should leave no
   * pending edit at all, and comparing against the already-edited text would
   * never notice that (§23 — the save state has to be true, not approximate).
   */
  readonly originalText: string;
  /**
   * False when rebuilding this paragraph from text would lose something —
   * a picture, a field, a footnote anchor. The UI must not offer to edit it.
   */
  readonly editable: boolean;
  /** Byte span in `word/document.xml`, used only by the save path. */
  readonly start: number;
  readonly end: number;
}

export interface DocxTableCell {
  readonly paragraphs: readonly DocxParagraph[];
  readonly colSpan: number;
  /** True for the continuation cells of a vertical merge, which render empty. */
  readonly merged: boolean;
}

export interface DocxTable {
  readonly kind: 'table';
  readonly rows: readonly (readonly DocxTableCell[])[];
  readonly start: number;
  readonly end: number;
}

export type DocxBlock = DocxParagraph | DocxTable;

export interface ParsedBody {
  readonly blocks: readonly DocxBlock[];
  /** Every paragraph in document order, including those inside tables. */
  readonly paragraphs: readonly DocxParagraph[];
  /** True when the block budget stopped the scan before the end (§33). */
  readonly truncated: boolean;
}

/* ------------------------------------------------------------------ *
 * XML text helpers
 * ------------------------------------------------------------------ */

/** Attribute value from a start tag, unescaped. */
function attr(tag: string, name: string): string | null {
  const pattern = new RegExp(`\\b${name.replace(':', '\\:')}="([^"]*)"`);
  const found = pattern.exec(tag);
  return found ? unescapeXml(found[1]!) : null;
}

/**
 * Find the end of the element that starts at `open`.
 *
 * Nesting matters: `<w:p>` may contain another `<w:p>` inside a table cell,
 * and a naive search for the next `</w:p>` would cut the outer one short.
 * Returns the offset just past the closing tag.
 */
function endOfElement(xml: string, open: number, name: string): number {
  const openTag = `<${name}`;
  const closeTag = `</${name}>`;
  // A self-closing start tag has no body at all.
  const tagEnd = xml.indexOf('>', open);
  if (tagEnd < 0) return xml.length;
  if (xml[tagEnd - 1] === '/') return tagEnd + 1;

  let depth = 1;
  let cursor = tagEnd + 1;
  while (depth > 0) {
    const nextOpen = xml.indexOf(openTag, cursor);
    const nextClose = xml.indexOf(closeTag, cursor);
    if (nextClose < 0) return xml.length;
    if (nextOpen >= 0 && nextOpen < nextClose) {
      const nextTagEnd = xml.indexOf('>', nextOpen);
      if (nextTagEnd < 0) return xml.length;
      // Only a real start tag increases depth — `<w:pPr` starts with `<w:p`.
      const afterName = xml[nextOpen + openTag.length]!;
      if (afterName === '>' || afterName === ' ' || afterName === '/') {
        if (xml[nextTagEnd - 1] !== '/') depth += 1;
      }
      cursor = nextTagEnd + 1;
      continue;
    }
    depth -= 1;
    cursor = nextClose + closeTag.length;
  }
  return cursor;
}

/** The `<w:body>` span, or the whole part when the document has no body. */
function bodySpan(xml: string): { start: number; end: number } {
  const open = xml.indexOf('<w:body');
  if (open < 0) return { start: 0, end: xml.length };
  const tagEnd = xml.indexOf('>', open);
  const close = xml.lastIndexOf('</w:body>');
  return {
    start: tagEnd < 0 ? open : tagEnd + 1,
    end: close < 0 ? xml.length : close,
  };
}

/* ------------------------------------------------------------------ *
 * run parsing
 * ------------------------------------------------------------------ */

interface RunFormat {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly vertical: 'super' | 'sub' | null;
  readonly highlight: string | null;
  readonly color: string | null;
}

const PLAIN: RunFormat = {
  bold: false, italic: false, underline: false, strike: false,
  vertical: null, highlight: null, color: null,
};

/**
 * `<w:b/>` means on; `<w:b w:val="0"/>` means off. The second form appears
 * whenever a style turns something on and a run turns it back off, so treating
 * the element's presence as truth would render half the document bold.
 */
function toggle(rPr: string, name: string): boolean {
  const found = new RegExp(`<w:${name}(\\s[^>]*)?/?>`).exec(rPr);
  if (!found) return false;
  const value = found[1] ? attr(found[1], 'w:val') : null;
  return value === null || !(value === '0' || value === 'false' || value === 'off');
}

function parseRunFormat(rPr: string | null): RunFormat {
  if (!rPr) return PLAIN;
  const vertAlign = /<w:vertAlign\b[^>]*w:val="([^"]+)"/.exec(rPr)?.[1];
  return {
    bold: toggle(rPr, 'b'),
    italic: toggle(rPr, 'i'),
    underline: /<w:u\b[^>]*w:val="(?!none)/.test(rPr),
    strike: toggle(rPr, 'strike'),
    vertical: vertAlign === 'superscript' ? 'super' : vertAlign === 'subscript' ? 'sub' : null,
    highlight: /<w:highlight\b[^>]*w:val="([^"]+)"/.exec(rPr)?.[1] ?? null,
    color: /<w:color\b[^>]*w:val="([^"]+)"/.exec(rPr)?.[1]?.replace(/^auto$/, '') || null,
  };
}

interface RunContext {
  readonly href: string | null;
  readonly revision: RevisionMark | null;
  readonly rels: ReadonlyMap<string, string>;
}

/** Parse one `<w:r>…</w:r>` into zero or more runs. */
function parseRun(xml: string, context: RunContext): DocxRun[] {
  const rPr = /<w:rPr\b[^>]*>([\s\S]*?)<\/w:rPr>/.exec(xml)?.[1] ?? null;
  const format = parseRunFormat(rPr);
  const out: DocxRun[] = [];

  // Walk the run's children in document order so a tab between two words does
  // not end up at the end of the paragraph.
  const CHILD = /<w:(t|delText|tab|br|cr|drawing|pict|object|footnoteReference|endnoteReference)\b([^>]*?)(?:\/>|>([\s\S]*?)<\/w:\1>)/g;
  for (const match of xml.matchAll(CHILD)) {
    const name = match[1]!;
    const body = match[3] ?? '';
    switch (name) {
      case 't':
      case 'delText':
        out.push({
          kind: 'text',
          text: unescapeXml(body),
          ...format,
          href: context.href,
          revision: name === 'delText' ? 'deleted' : context.revision,
        });
        break;
      case 'tab':
        out.push({ kind: 'tab' });
        break;
      case 'br':
      case 'cr':
        out.push({ kind: 'break' });
        break;
      case 'drawing':
      case 'pict':
      case 'object': {
        const relId = /r:embed="([^"]+)"/.exec(body)?.[1]
          ?? /r:id="([^"]+)"/.exec(body)?.[1]
          ?? '';
        const alt = /<wp:docPr\b[^>]*\bdescr="([^"]*)"/.exec(body)?.[1]
          ?? /<wp:docPr\b[^>]*\bname="([^"]*)"/.exec(body)?.[1]
          ?? '';
        out.push({
          kind: 'image',
          relId,
          part: relId ? context.rels.get(relId) ?? null : null,
          widthEmu: Number(/<wp:extent\b[^>]*\bcx="(\d+)"/.exec(body)?.[1] ?? 0),
          heightEmu: Number(/<wp:extent\b[^>]*\bcy="(\d+)"/.exec(body)?.[1] ?? 0),
          alt: unescapeXml(alt),
        });
        break;
      }
      case 'footnoteReference':
      case 'endnoteReference':
        // Rendered as a marker; the note text lives in another part which this
        // release does not display. The paragraph is marked uneditable so the
        // reference cannot be lost by a text edit.
        out.push({ kind: 'field', instruction: name, text: '※' });
        break;
    }
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * paragraph parsing
 * ------------------------------------------------------------------ */

export interface ParseOptions {
  /** Style id → display name, from `word/styles.xml`. */
  readonly styleNames: ReadonlyMap<string, string>;
  /** Numbering id → ordered/bulleted, from `word/numbering.xml`. */
  readonly orderedNumbering: ReadonlySet<string>;
  /** Relationship id → part name, from `word/_rels/document.xml.rels`. */
  readonly rels: ReadonlyMap<string, string>;
  /** §33 — stop scanning past this many blocks rather than freezing. */
  readonly maxBlocks: number;
}

function headingLevelOf(styleId: string | null, styleName: string | null): number | null {
  const source = styleId ?? styleName ?? '';
  const fromId = /^Heading(\d)$/i.exec(source.replace(/\s+/g, ''));
  if (fromId) return Number(fromId[1]);
  const fromName = /^heading\s*(\d)$/i.exec(styleName ?? '');
  if (fromName) return Number(fromName[1]);
  if (/^Title$/i.test(source)) return 1;
  return null;
}

function parseParagraph(
  xml: string, start: number, end: number, index: number, options: ParseOptions,
): DocxParagraph {
  const source = xml.slice(start, end);
  const pPr = /<w:pPr\b[^>]*>([\s\S]*?)<\/w:pPr>/.exec(source)?.[1] ?? '';

  const styleId = /<w:pStyle\b[^>]*w:val="([^"]+)"/.exec(pPr)?.[1] ?? null;
  const styleName = styleId ? options.styleNames.get(styleId) ?? null : null;
  const alignmentRaw = /<w:jc\b[^>]*w:val="([^"]+)"/.exec(pPr)?.[1];
  const alignment = alignmentRaw === 'center' ? 'center'
    : alignmentRaw === 'right' || alignmentRaw === 'end' ? 'right'
      : alignmentRaw === 'both' || alignmentRaw === 'distribute' ? 'justify'
        : alignmentRaw === 'left' || alignmentRaw === 'start' ? 'left'
          : null;

  const numId = /<w:numId\b[^>]*w:val="([^"]+)"/.exec(pPr)?.[1] ?? null;
  const ilvl = /<w:ilvl\b[^>]*w:val="(\d+)"/.exec(pPr)?.[1];

  const runs: DocxRun[] = [];

  // Walk hyperlinks, revision containers and bare runs in document order.
  const CONTAINER = /<w:(hyperlink|ins|del|r)\b([^>]*?)(?:\/>|>([\s\S]*?)<\/w:\1>)/g;
  for (const match of source.matchAll(CONTAINER)) {
    const name = match[1]!;
    const tagAttrs = match[2] ?? '';
    const body = match[3] ?? '';

    if (name === 'r') {
      runs.push(...parseRun(body, { href: null, revision: null, rels: options.rels }));
      continue;
    }

    const href = name === 'hyperlink'
      ? options.rels.get(attr(tagAttrs, 'r:id') ?? '') ?? attr(tagAttrs, 'w:anchor')
      : null;
    const revision: RevisionMark | null = name === 'ins' ? 'inserted' : name === 'del' ? 'deleted' : null;

    for (const inner of body.matchAll(/<w:r\b(?:[^>]*?)(?:\/>|>([\s\S]*?)<\/w:r>)/g)) {
      runs.push(...parseRun(inner[1] ?? '', { href, revision, rels: options.rels }));
    }
  }

  const text = runs.map((r) => (
    r.kind === 'text' ? r.text : r.kind === 'tab' ? '\t' : r.kind === 'break' ? '\n' : ''
  )).join('');

  // A paragraph is editable only when everything in it can be written back
  // from plain text. Anything else is preserved by not touching it.
  const editable = runs.every((r) => r.kind === 'text' || r.kind === 'tab' || r.kind === 'break')
    && !runs.some((r) => r.kind === 'text' && r.revision !== null)
    && !/<w:fldChar|<w:instrText|<w:sdt\b|<w:commentRangeStart/.test(source);

  return {
    kind: 'paragraph',
    index,
    styleId,
    styleName,
    headingLevel: headingLevelOf(styleId, styleName),
    alignment,
    listLevel: numId ? Number(ilvl ?? 0) : null,
    listOrdered: numId ? options.orderedNumbering.has(numId) : false,
    runs,
    text,
    originalText: text,
    editable,
    start,
    end,
  };
}

/* ------------------------------------------------------------------ *
 * body scanning
 * ------------------------------------------------------------------ */

export function parseBody(xml: string, options: ParseOptions): ParsedBody {
  const { start: bodyStart, end: bodyEnd } = bodySpan(xml);
  const blocks: DocxBlock[] = [];
  const paragraphs: DocxParagraph[] = [];
  let truncated = false;

  let cursor = bodyStart;

  /**
   * The next table, remembered between iterations.
   *
   * Searching for `<w:tbl` on every pass is quadratic, and quadratic in a way
   * that only shows up on real documents: a miss scans to the end of the part
   * before returning −1, so a twenty-thousand-paragraph document with no
   * tables scans the whole body twenty thousand times. Measured at 8.3 seconds
   * before this cache and 90 milliseconds after (§33).
   *
   * `-1` means "no table at or after `tableSearchedFrom`", which stays true as
   * the cursor advances, so the search is never repeated.
   */
  let nextTbl = indexOfElement(xml, '<w:tbl', bodyStart, bodyEnd);

  while (cursor < bodyEnd) {
    if (blocks.length >= options.maxBlocks) { truncated = true; break; }

    const nextP = indexOfElement(xml, '<w:p', cursor, bodyEnd);
    if (nextTbl >= 0 && nextTbl < cursor) {
      nextTbl = indexOfElement(xml, '<w:tbl', cursor, bodyEnd);
    }
    if (nextP < 0 && nextTbl < 0) break;

    if (nextTbl >= 0 && (nextP < 0 || nextTbl < nextP)) {
      const end = endOfElement(xml, nextTbl, 'w:tbl');
      const table = parseTable(xml, nextTbl, end, paragraphs, options);
      blocks.push(table);
      cursor = end;
      continue;
    }

    const end = endOfElement(xml, nextP, 'w:p');
    const paragraph = parseParagraph(xml, nextP, end, paragraphs.length, options);
    paragraphs.push(paragraph);
    blocks.push(paragraph);
    cursor = end;
  }

  return { blocks, paragraphs, truncated };
}

/**
 * Find a start tag, skipping names that merely share a prefix.
 *
 * `<w:pPr>` starts with `<w:p`, and `<w:tblPr>` with `<w:tbl`. Matching those
 * would nest the scanner inside a property element and lose the rest of the
 * document.
 */
function indexOfElement(xml: string, tag: string, from: number, limit: number): number {
  let cursor = from;
  for (;;) {
    const found = xml.indexOf(tag, cursor);
    if (found < 0 || found >= limit) return -1;
    const after = xml[found + tag.length];
    if (after === '>' || after === ' ' || after === '/' || after === '\n' || after === '\r' || after === '\t') {
      return found;
    }
    cursor = found + tag.length;
  }
}

function parseTable(
  xml: string, start: number, end: number,
  paragraphs: DocxParagraph[], options: ParseOptions,
): DocxTable {
  const rows: DocxTableCell[][] = [];
  let rowCursor = start;

  while (rowCursor < end) {
    const rowStart = indexOfElement(xml, '<w:tr', rowCursor, end);
    if (rowStart < 0) break;
    const rowEnd = endOfElement(xml, rowStart, 'w:tr');

    const cells: DocxTableCell[] = [];
    let cellCursor = rowStart;
    while (cellCursor < rowEnd) {
      const cellStart = indexOfElement(xml, '<w:tc', cellCursor, rowEnd);
      if (cellStart < 0) break;
      const cellEnd = endOfElement(xml, cellStart, 'w:tc');
      const cellXml = xml.slice(cellStart, cellEnd);

      const cellParagraphs: DocxParagraph[] = [];
      let inner = cellStart;
      while (inner < cellEnd) {
        const pStart = indexOfElement(xml, '<w:p', inner, cellEnd);
        if (pStart < 0) break;
        const pEnd = endOfElement(xml, pStart, 'w:p');
        const paragraph = parseParagraph(xml, pStart, pEnd, paragraphs.length, options);
        paragraphs.push(paragraph);
        cellParagraphs.push(paragraph);
        inner = pEnd;
      }

      const vMerge = /<w:vMerge\b([^>]*)\/?>/.exec(cellXml);
      cells.push({
        paragraphs: cellParagraphs,
        colSpan: Number(/<w:gridSpan\b[^>]*w:val="(\d+)"/.exec(cellXml)?.[1] ?? 1),
        merged: vMerge !== null && attr(vMerge[1] ?? '', 'w:val') !== 'restart',
      });
      cellCursor = cellEnd;
    }

    rows.push(cells);
    rowCursor = rowEnd;
  }

  return { kind: 'table', rows, start, end };
}

/* ------------------------------------------------------------------ *
 * side parts
 * ------------------------------------------------------------------ */

/** Style id → display name. */
export function parseStyleNames(stylesXml: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const match of stylesXml.matchAll(/<w:style\b([^>]*)>([\s\S]*?)<\/w:style>/g)) {
    const id = attr(match[1]!, 'w:styleId');
    const name = /<w:name\b[^>]*w:val="([^"]*)"/.exec(match[2]!)?.[1];
    if (id && name) out.set(id, unescapeXml(name));
  }
  return out;
}

/**
 * Numbering ids whose format is a counter rather than a bullet.
 *
 * `w:num` points at an abstract definition, and only that definition says
 * whether level 0 is decimal or a bullet — so both parts have to be read to
 * decide whether a list renders as `1.` or `•`.
 */
export function parseOrderedNumbering(numberingXml: string): Set<string> {
  const abstractFormat = new Map<string, string>();
  for (const match of numberingXml.matchAll(/<w:abstractNum\b([^>]*)>([\s\S]*?)<\/w:abstractNum>/g)) {
    const id = attr(match[1]!, 'w:abstractNumId');
    const firstLevel = /<w:lvl\b[^>]*w:ilvl="0"[\s\S]*?<w:numFmt\b[^>]*w:val="([^"]+)"/.exec(match[2]!)?.[1]
      ?? /<w:numFmt\b[^>]*w:val="([^"]+)"/.exec(match[2]!)?.[1];
    if (id && firstLevel) abstractFormat.set(id, firstLevel);
  }

  const ordered = new Set<string>();
  for (const match of numberingXml.matchAll(/<w:num\b([^>]*)>([\s\S]*?)<\/w:num>/g)) {
    const numId = attr(match[1]!, 'w:numId');
    const abstractId = /<w:abstractNumId\b[^>]*w:val="([^"]+)"/.exec(match[2]!)?.[1];
    if (!numId || !abstractId) continue;
    const format = abstractFormat.get(abstractId);
    if (format && format !== 'bullet' && format !== 'none') ordered.add(numId);
  }
  return ordered;
}

/**
 * Relationship id → package part name, resolved against the referring part's
 * folder.
 *
 * Targets are relative, and PowerPoint in particular writes `../media/x.png`
 * from `ppt/slides/`. Concatenating without resolving `..` produces
 * `ppt/slides/../media/x.png`, which matches no entry in the package — so the
 * picture silently disappears and the slide renders without it.
 */
export function parseRelationships(relsXml: string, base = 'word/'): Map<string, string> {
  const out = new Map<string, string>();
  for (const match of relsXml.matchAll(/<Relationship\b[^>]*\/?>/g)) {
    const tag = match[0];
    const id = attr(tag, 'Id');
    const target = attr(tag, 'Target');
    const mode = attr(tag, 'TargetMode');
    if (!id || !target) continue;
    // External targets are URLs, not parts, and must stay untouched — they are
    // never fetched (§29).
    if (mode === 'External' || /^[a-z]+:/i.test(target)) { out.set(id, target); continue; }
    out.set(id, target.startsWith('/') ? normalizePartPath(target.slice(1))
      : normalizePartPath(`${base}${target}`));
  }
  return out;
}

/**
 * Collapse `.` and `..` in a package path.
 *
 * A `..` that would escape the package is dropped rather than kept: an OPC
 * part name has no parent above the root, and a path that tries to climb out
 * of one is malformed at best and an attempt at traversal at worst (§21).
 */
export function normalizePartPath(path: string): string {
  const out: string[] = [];
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') { out.pop(); continue; }
    out.push(segment);
  }
  return out.join('/');
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface ParagraphEdit {
  readonly index: number;
  readonly text: string;
}

export interface EditResult {
  readonly xml: string;
  /** Descriptions of anything this rewrite could not carry over (§13). */
  readonly dropped: readonly string[];
  readonly failed: readonly number[];
}

/**
 * Rewrite the text of specific paragraphs, leaving the rest of the part alone.
 *
 * The paragraph's own properties (`<w:pPr>`) are kept verbatim, so style,
 * alignment, list membership, spacing and borders survive. The new text takes
 * the formatting of the paragraph's first run, which is the only honest choice
 * when several differently-formatted runs collapse into one string — and it is
 * reported in `dropped` rather than left for the user to discover.
 *
 * Edits are applied back to front so that each replacement's offsets are still
 * valid when it happens.
 */
export function applyParagraphEdits(
  xml: string, paragraphs: readonly DocxParagraph[], edits: readonly ParagraphEdit[],
): EditResult {
  const dropped: string[] = [];
  const failed: number[] = [];
  const byIndex = new Map(paragraphs.map((p) => [p.index, p]));

  const ordered = [...edits].sort((a, b) => {
    const left = byIndex.get(a.index)?.start ?? -1;
    const right = byIndex.get(b.index)?.start ?? -1;
    return right - left;
  });

  let out = xml;
  for (const edit of ordered) {
    const paragraph = byIndex.get(edit.index);
    if (!paragraph) { failed.push(edit.index); continue; }
    if (!paragraph.editable) { failed.push(edit.index); continue; }

    const source = out.slice(paragraph.start, paragraph.end);
    // The span must still hold the paragraph it did when parsed; if the caller
    // handed us different bytes, refusing is better than corrupting them.
    if (!source.startsWith('<w:p')) { failed.push(edit.index); continue; }

    const formats = new Set(
      paragraph.runs
        .filter((r) => r.kind === 'text')
        .map((r) => JSON.stringify([r.bold, r.italic, r.underline, r.strike, r.vertical, r.color, r.highlight])),
    );
    if (formats.size > 1) {
      dropped.push(`문단 ${edit.index + 1}: 문단 안의 서로 다른 글자 서식이 첫 서식으로 통일됨`);
    }
    if (paragraph.runs.some((r) => r.kind === 'text' && r.href !== null)) {
      dropped.push(`문단 ${edit.index + 1}: 하이퍼링크가 일반 텍스트로 바뀜`);
    }

    out = out.slice(0, paragraph.start) + rebuildParagraph(source, edit.text) + out.slice(paragraph.end);
  }

  return { xml: out, dropped, failed };
}

function rebuildParagraph(source: string, text: string): string {
  const openTagEnd = source.indexOf('>');
  if (openTagEnd < 0) throw new HandlerError('error.unableToRead', 'malformed paragraph element');
  const openTag = source.slice(0, openTagEnd + 1).replace(/\/>$/, '>');

  const pPr = /<w:pPr\b[^>]*>[\s\S]*?<\/w:pPr>|<w:pPr\b[^>]*\/>/.exec(source)?.[0] ?? '';
  const rPr = /<w:r\b[^>]*>[\s\S]*?(<w:rPr\b[^>]*>[\s\S]*?<\/w:rPr>)/.exec(source)?.[1] ?? '';

  // A line break inside the text must become `<w:br/>`, not a literal newline:
  // WordprocessingML collapses whitespace, so a newline would simply vanish.
  const body = text.split('\n').map((line, i) => {
    const parts = line.split('\t').map((segment) => (
      segment === '' ? '' : `<w:t xml:space="preserve">${escapeXml(segment)}</w:t>`
    )).join('<w:tab/>');
    return (i === 0 ? '' : '<w:br/>') + parts;
  }).join('');

  return `${openTag}${pPr}<w:r>${rPr}${body}</w:r></w:p>`;
}
